import type { CommercialTrialResult } from "@itaas/pep-contracts";
import { SUPPORT_CONTACT_EMAIL, TECH_SUPPORT_PHONE } from "../../config";

interface TrialActivatedPanelProps {
  trialResult: CommercialTrialResult;
  biometricMessage: string | null;
  onContinue: () => void;
}

function CredentialRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  if (!value?.trim()) return null;
  return (
    <div className="pep-trial-credential-row">
      <span className="pep-trial-credential-label">{label}</span>
      <span className={`pep-trial-credential-value${mono ? " font-monospace" : ""}`}>{value}</span>
    </div>
  );
}

export function TrialActivatedPanel({
  trialResult,
  biometricMessage,
  onContinue,
}: TrialActivatedPanelProps) {
  return (
    <div className="card border-0 shadow-sm p-4 p-md-5">
      <div className="text-center mb-4">
        <div className="display-6 text-success mb-2">✓</div>
        <h2 className="h4 fw-bold">Essai activé</h2>
        <p className="text-secondary mb-0">
          Votre organisation est prête. Conservez ces identifiants avant de continuer.
        </p>
      </div>

      <div className="pep-trial-credentials mb-4">
        <CredentialRow label="Organisation" value={trialResult.tenantSlug} mono />
        <CredentialRow label="Email de connexion" value={trialResult.adminEmail} mono />
        <CredentialRow label="Email contact" value={trialResult.contactEmail} />
        <CredentialRow label="Mot de passe temporaire" value={trialResult.temporaryPassword} mono />
        <CredentialRow
          label="Fin d'essai"
          value={new Date(trialResult.trialEndsAt).toLocaleDateString("fr-FR")}
        />
      </div>

      <p className="small text-center mb-3">
        Utilisez l&apos;<strong>email de connexion</strong> (admin@…) — pas l&apos;email contact —
        sur la page de connexion ITaaS.
      </p>

      {trialResult.credentialsEmailSent ? (
        <p className="small text-success text-center mb-3">
          Un récapitulatif a été envoyé à <strong>{trialResult.contactEmail}</strong>.
        </p>
      ) : (
        <p className="small text-warning text-center mb-3">
          Email non expédié — notez vos identifiants ci-dessus ou contactez{" "}
          <a href={`mailto:${SUPPORT_CONTACT_EMAIL}`}>{SUPPORT_CONTACT_EMAIL}</a>.
        </p>
      )}

      {biometricMessage && (
        <div className="alert alert-info mb-4" role="status">
          <h3 className="h6 fw-bold mb-2">Lecteur(s) biométrique</h3>
          <p className="mb-2 small">{biometricMessage}</p>
          <p className="mb-0 small">
            Support :{" "}
            <a href={`tel:${TECH_SUPPORT_PHONE.replace(/\s/g, "")}`}>{TECH_SUPPORT_PHONE}</a>
            {" · "}
            <a href={`mailto:${SUPPORT_CONTACT_EMAIL}`}>{SUPPORT_CONTACT_EMAIL}</a>
          </p>
        </div>
      )}

      <div className="text-center">
        <p className="text-secondary small mb-3">
          Changez votre mot de passe après la première connexion.
        </p>
        <button type="button" className="btn btn-primary btn-lg" onClick={onContinue}>
          Continuer vers la connexion →
        </button>
        <p className="small text-muted text-center mt-2 mb-0">
          Votre email de connexion sera pré-rempli — saisissez le mot de passe temporaire ci-dessus.
        </p>
      </div>
    </div>
  );
}
